import React from "react";
import { Navigate, Outlet } from "react-router-dom";

import { useOrganization } from "../../hooks/useOrganization";
import { OrganizationStatusEnum } from "../../types/organization/OrganizationStatusEnum";

/**
 * Route-level guard that bounces users out of org-scoped dashboard
 * pages when the active organization is in a non-ACTIVE state
 * (SUSPENDED or CANCELED).
 *
 * The backend allow-lists only `GET /organizations/:orgId` for
 * suspended orgs; every other org-scoped endpoint (members, seats,
 * billing, usage) returns 403 "Organization is suspended". Rendering
 * those dashboards for a suspended org produces a broken page full of
 * forbidden errors — this component short-circuits to the recovery
 * landing page at `/muggleTestV0/dashboard/my-orgs` instead.
 *
 * IMPORTANT: the my-orgs recovery page itself must live OUTSIDE this
 * guard, otherwise suspended-org users land on it and get bounced right
 * back to it in an infinite loop.
 *
 * Mount as a layout route wrapping `/dashboard/organization/*`:
 *
 * ```tsx
 * <Route element={<SuspendedOrgRedirect />}>
 *   <Route path="organization" element={<OrganizationOverviewPage />} />
 *   ...
 * </Route>
 * ```
 *
 * Rendering nothing while the context is still loading is intentional:
 * the parent DashboardLayout already shows a page loader, and flashing
 * an empty page before the real content lands is the lesser evil
 * compared to flashing the org dashboard and then yanking it away.
 */
export const SuspendedOrgRedirect: React.FC = () => {
  const { organization, loading } = useOrganization();

  if (loading) {
    return null;
  }

  if (organization && organization.status !== OrganizationStatusEnum.ACTIVE) {
    return <Navigate to="/muggleTestV0/dashboard/my-orgs" replace />;
  }

  return <Outlet />;
};
